// Feature P4: staff. Hire a crew, send them out on a contract with some of
// your gear, and settle their job when you finish (or knock off) your own.
//
// Pure, like economy.ts: every verb takes a SaveGame and returns a TxResult
// holding a NEW SaveGame; the input is never mutated. Everything random is
// seeded (the save's crew seed × the day for candidates, × the contract and
// assignment for a job), so the same save resolves the same way every time.
//
// A crew job is settled with the player's own contract maths: fee, tipPct,
// starsFor, repForStars, the same per-hit penalties, and tipBreakdown for the
// reasons ("Dazza clipped the fence twice"). What's crew-only: thoroughness
// comes from skill ± noise, collisions are a Poisson draw, time is par ×
// (1.2 − skill/2), wages come off the top and rep is halved (DECISIONS.md).

import type { Contract, EquipmentModel, JobResult, Layout, OwnedEquipment, PropKind, ZoneId } from './types';
import {
  BATTERY_WEAR_X, FUEL_AUD_PER_L, MODELS, chargeOf, money, repLevel, runSecondsOf, usesCharge, type SaveGame, type TxResult,
} from './economy';
import {
  NEEDS_RIDE_ON_COPY, OBSTACLE_PENALTY_AUD, VIOLATION_PENALTY_AUD, estimateParInput, needsRideOn, parSeconds, repForStars, starsFor, taskGearOk, tipPct,
} from './contract';
import { MAJOR_HIT_PENALTY_AUD, damageFor } from './props';
import { contractLayout } from './mutations';
import { layoutStats } from './layout';
import { hashString, mixSeed, mulberry32, pick, randInt, type Rng } from './rng';
import { tipBreakdown } from './tip';

// ─── Types ──────────────────────────────────────────────────────────────

export interface Candidate {
  id: string;
  /** "Dazza Mulgrew" */
  name: string;
  /** "Dazza": what the copy calls them. */
  short: string;
  /** Two lines, dry. */
  bio: [string, string];
  /** 1..3 when hired off the list; up to MAX_CREW_LEVEL with experience. */
  level: number;
  /** 0..1. Drives thoroughness and collisions. */
  skill: number;
  /** A$ per hour on the timesheet. */
  rateAud: number;
}

export interface Employee extends Candidate {
  /** Experience points: 1 per job, 2 for a three-star job. */
  xp: number;
  jobs: number;
  hiredDay: number;
}

export interface CrewAssignment {
  id: string;
  employeeId: string;
  /** A snapshot: the board turns over, the job they're on doesn't. */
  contract: Contract;
  /** Owned uids they took. Locked out of the player's loadout until they're back. */
  gear: string[];
}

/** One line of the payouts board: your job or a crew job, settled. */
export interface PayoutRow {
  id: string;
  who: 'you' | 'crew';
  employeeId?: string;
  /** "You" or the crew member's short name. */
  name: string;
  contractId: string;
  layoutKey: string;
  feeAud: number;
  tipPct: number;
  tipAud: number;
  penaltiesAud: number;
  /** Wages paid (0 for you). */
  wageAud: number;
  /** Fuel the crew bought on the road (0 for you). */
  fuelAud: number;
  /** What landed in the account. */
  netAud: number;
  stars: number;
  repPoints: number;
  abandoned: boolean;
  /** "Dazza clipped the fence twice" */
  lines?: string[];
}

export interface StaffState {
  /** Per-save seed for candidates and crew jobs. */
  seed: number;
  /** The day `taken` belongs to; a new day clears it. */
  day: number;
  /** Candidate ids already hired today (they leave the list). */
  taken: string[];
  hired: Employee[];
  assignments: CrewAssignment[];
  /** Most recent last, at most PAYOUTS_KEEP. */
  payouts: PayoutRow[];
  /** Counter for employee, assignment and payout ids. */
  nextId: number;
}

/** A settled crew job, before it is banked by applyCrewResults. */
export interface CrewResult {
  assignmentId: string;
  employeeId: string;
  short: string;
  contract: Contract;
  /** The same shape the player's job produces; `gear` carries fuel and wear home. */
  result: JobResult;
  /** Hours on the timesheet. */
  hours: number;
  wageAud: number;
  fuelAud: number;
  /** result.totalAud − wages − fuel. Can be negative. */
  netAud: number;
  /** Half the player's rep for the same stars (floored). */
  repPoints: number;
  /** Named deduction lines. */
  lines: string[];
}

// ─── Numbers ────────────────────────────────────────────────────────────

/** Crew unlocks at this rep level. */
export const CREW_REP_LEVEL = 2;
/** Staff cap by rep level (index = level): 1 at Rep 2, 2 at Rep 4, 3 at Rep 6. */
export const CREW_CAP = [0, 0, 1, 1, 2, 2, 3] as const;
export const CANDIDATES_PER_DAY = 3;
/** Timesheet hours per minute of yard time: a 5-minute par backyard is 3¾ hours on the clock (DECISIONS.md). */
export const CREW_HOURS_PER_PAR_MIN = 0.75;
/** A crew member takes at most this much gear (their own ute). */
export const CREW_GEAR_MAX = 3;
/** XP at which levels 1..5 begin. */
export const CREW_LEVEL_XP = [0, 5, 12, 22, 35] as const;
export const MAX_CREW_LEVEL = CREW_LEVEL_XP.length;
export const LEVEL_SKILL_STEP = 0.02;
export const LEVEL_RATE_STEP = 2;
export const SKILL_MAX = 0.98;
export const PAYOUTS_KEEP = 20;
/** Engine condition lost per minute of running (roughly what a careful player's job costs). */
export const CREW_ENGINE_WEAR_PER_MIN = 0.004;
/** Blade wear per mowable cell, as the sim's BLADE_WEAR_PER_CELL, × 1.5 passes. */
export const CREW_BLADE_WEAR_PER_CELL = 0.000015;
/** Share of a collision that lands in a do-not-mow zone, on a mowing job with one. */
export const CREW_ZONE_SHARE = 0.1;
/** Crew tip % this far over yours earns the nudge. */
export const NUDGE_GAP_PCT = 8;

/** Skill, rate and odds for a candidate at each level. */
const LEVEL_TABLE: Record<1 | 2 | 3, { skill: [number, number]; rate: [number, number] }> = {
  1: { skill: [0.78, 0.86], rate: [28, 31] },
  2: { skill: [0.85, 0.92], rate: [31, 35] },
  3: { skill: [0.91, 0.96], rate: [34, 38] },
};

// ─── Names and bios (invented) ──────────────────────────────────────────

const SHORTS = [
  'Dazza', 'Shazza', 'Bazza', 'Macca', 'Robbo', 'Gazza', 'Tezza', 'Davo', 'Kez', 'Jonno',
  'Stevo', 'Bec', 'Mick', 'Tash', 'Wazza', 'Fitzy', 'Jacko', 'Deb', 'Smithy', 'Nugget',
];
const SURNAMES = [
  'Mulgrew', 'Birchall', 'Tunstall', 'Kettering', 'Pobjoy', 'Harrowby', 'Quinnell', 'Dunmore',
  'Ashcombe', 'Fennimore', 'Galbraith', 'Wetherall', 'Coldstream', 'Pennicott', 'Ravenhall', 'Lockyer',
];
const BIO_PAST = [
  'Six years on a council crew.',
  'Ran an uncle’s turf farm till it sold.',
  'Mowed the local footy oval for free-ish.',
  'Came down off a banana farm up north.',
  'Three summers at a caravan park, mowing round annexes.',
  'Used to drive a forklift. Reckons a ride-on is the same thing.',
  'Got a start whipper-snipping the school fence line.',
  'Once edged a bowls green to the club’s satisfaction. Once.',
  'Knows every nature strip in Kerbside by first name.',
  'Left the mines for a quieter life. Found mowers.',
  'Did a season on a golf course. Won’t talk about the fourteenth.',
  'Grew up next to a turf farm. Smells of kikuyu, faintly.',
];
const BIO_QUIRK = [
  'Brings a thermos. Won’t share it.',
  'Reckons the fence moved first.',
  'Starts any mower on the second pull. Won’t say how.',
  'Smoko is at ten sharp, not a minute later.',
  'Talks to the magpies. They listen.',
  'Wears thongs on the job. We’ve had words.',
  'Stripes so straight the neighbours check with a ruler.',
  'Has opinions on two-stroke mix. Many opinions.',
  'Once lost a whole tarp in a southerly.',
  'Won’t mow near a trampoline. Won’t say why.',
  'Keeps a pie in the glovebox for emergencies.',
  'Says “she’ll be right” about everything, the letterbox included.',
];

// ─── Small maths ────────────────────────────────────────────────────────

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const round2 = (x: number) => Math.round(x * 100) / 100;

/** Standard normal (Box–Muller) from the seeded rng. */
export function gauss(rng: Rng): number {
  const u = 1 - rng();
  const v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Poisson draw (Knuth). λ ≤ 0 is 0. */
export function poisson(rng: Rng, lambda: number): number {
  if (!(lambda > 0)) return 0;
  const l = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= rng();
  } while (p > l && k < 1000);
  return k - 1;
}

/** Thoroughness noise (fraction): a sloppier hand is less predictable. */
export function thoroughnessSigma(skill: number): number {
  return 0.25 * (1 - skill) + 0.005;
}

/** Thoroughness 0..100 = 100 × (skill ± noise), clamped. */
export function crewThoroughness(rng: Rng, skill: number): number {
  return Math.round(1000 * clamp01(skill + gauss(rng) * thoroughnessSigma(skill))) / 10;
}

/** Expected collisions: (1 − skill) × (2 + 2 × complexity). A 2★ yard at 80% skill is 1.2. */
export function collisionLambda(skill: number, complexity: number): number {
  return Math.max(0, 1 - skill) * (2 + 2 * Math.max(1, complexity));
}

/** Crew time = par × (1.2 − skill / 2). */
export function crewSeconds(par: number, skill: number): number {
  return par * (1.2 - skill / 2);
}

/** Wages = timesheet hours × rate, to the cent. Timesheet hours = yard minutes × CREW_HOURS_PER_PAR_MIN. */
export function crewWage(seconds: number, rateAud: number): { hours: number; wageAud: number } {
  const hours = (seconds / 60) * CREW_HOURS_PER_PAR_MIN;
  return { hours, wageAud: money(hours * rateAud) };
}

/** How many staff this rep allows. */
export function crewCap(repPoints: number): number {
  return CREW_CAP[Math.min(CREW_CAP.length - 1, repLevel(repPoints))] ?? 0;
}

export function crewUnlocked(repPoints: number): boolean {
  return repLevel(repPoints) >= CREW_REP_LEVEL;
}

/** The rep level at which the cap next rises (null at the top). */
export function nextCapLevel(repPoints: number): number | null {
  const cap = crewCap(repPoints);
  const i = CREW_CAP.findIndex((c) => c > cap);
  return i < 0 ? null : i;
}

/** "twice", "3 times" (nothing for once). */
function times(n: number): string {
  return n === 1 ? '' : n === 2 ? ' twice' : ` ${n} times`;
}

// ─── Save ───────────────────────────────────────────────────────────────

/** A fresh crew state; the seed comes from the save so migration is deterministic. */
export function emptyStaff(seed: number): StaffState {
  return { seed: seed >>> 0 || 1, day: 0, taken: [], hired: [], assignments: [], payouts: [], nextId: 1 };
}

function seedFor(save: SaveGame): number {
  return hashString(`crew|${save.companyName}|${save.nextUid}`) || 1;
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/**
 * Old saves have no `staff`; a malformed one is rebuilt. Hired staff and
 * payouts survive; an assignment survives only if its employee is hired and
 * every piece of its gear is still owned.
 */
export function migrateStaff(save: SaveGame): SaveGame {
  const raw = save.staff as Partial<StaffState> | undefined;
  if (!raw || typeof raw !== 'object') return { ...save, staff: emptyStaff(seedFor(save)) };
  const hired = (Array.isArray(raw.hired) ? raw.hired : []).filter(
    (e): e is Employee => !!e && typeof e.id === 'string' && typeof e.name === 'string' && isNum(e.skill) && isNum(e.rateAud),
  ).map((e) => ({
    ...e,
    short: typeof e.short === 'string' ? e.short : e.name.split(' ')[0],
    bio: (Array.isArray(e.bio) && e.bio.length === 2 ? e.bio : ['', '']) as [string, string],
    level: isNum(e.level) ? Math.max(1, Math.min(MAX_CREW_LEVEL, Math.round(e.level))) : 1,
    skill: clamp01(e.skill),
    xp: isNum(e.xp) ? e.xp : 0,
    jobs: isNum(e.jobs) ? e.jobs : 0,
    hiredDay: isNum(e.hiredDay) ? e.hiredDay : 0,
  }));
  const ids = new Set(hired.map((e) => e.id));
  const owned = new Set(save.owned.map((o) => o.uid));
  const assignments = (Array.isArray(raw.assignments) ? raw.assignments : []).filter(
    (a): a is CrewAssignment => !!a && typeof a.id === 'string' && ids.has(a.employeeId) && !!a.contract
      && typeof a.contract.id === 'string' && Array.isArray(a.gear) && a.gear.every((u) => owned.has(u)),
  );
  const payouts = (Array.isArray(raw.payouts) ? raw.payouts : []).filter(
    (p): p is PayoutRow => !!p && (p.who === 'you' || p.who === 'crew') && isNum(p.netAud),
  ).slice(-PAYOUTS_KEEP);
  return {
    ...save,
    staff: {
      seed: isNum(raw.seed) && raw.seed ? raw.seed >>> 0 : seedFor(save),
      day: isNum(raw.day) ? raw.day : 0,
      taken: Array.isArray(raw.taken) ? raw.taken.filter((t) => typeof t === 'string') : [],
      hired,
      assignments,
      payouts,
      nextId: isNum(raw.nextId) ? raw.nextId : hired.length + assignments.length + payouts.length + 1,
    },
  };
}

/** The crew state of any save (old saves read as an empty crew). */
export function staffOf(save: SaveGame): StaffState {
  return save.staff ?? emptyStaff(seedFor(save));
}

function cloneStaff(save: SaveGame): { s: SaveGame; st: StaffState } {
  const st0 = staffOf(save);
  const st: StaffState = {
    ...st0,
    taken: st0.taken.slice(),
    hired: st0.hired.map((e) => ({ ...e, bio: [e.bio[0], e.bio[1]] as [string, string] })),
    assignments: st0.assignments.map((a) => ({ ...a, gear: a.gear.slice() })),
    payouts: st0.payouts.slice(),
  };
  const s: SaveGame = { ...save, owned: save.owned.map((o) => ({ ...o })), history: save.history.slice(), staff: st };
  return { s, st };
}

const refuse = (save: SaveGame, reason: string): TxResult => ({ ok: false, save, reason, costAud: 0 });

// ─── Candidates ─────────────────────────────────────────────────────────

/**
 * The day's three candidates: seeded by the save's crew seed and the day, so
 * they hold all day and rotate at midnight. Nobody shares a short name with
 * the hired crew. Candidates already hired today are left off.
 */
export function candidatesFor(save: SaveGame, day: number): Candidate[] {
  const st = staffOf(save);
  const rng = mulberry32(mixSeed(st.seed, day | 0));
  const used = new Set(st.hired.map((e) => e.short));
  const out: Candidate[] = [];
  for (let i = 0; i < CANDIDATES_PER_DAY; i++) {
    let short = pick(rng, SHORTS);
    for (let t = 0; t < SHORTS.length && used.has(short); t++) short = SHORTS[(SHORTS.indexOf(short) + 1) % SHORTS.length];
    used.add(short);
    const r = rng();
    const level = (r < 0.5 ? 1 : r < 0.85 ? 2 : 3) as 1 | 2 | 3;
    const row = LEVEL_TABLE[level];
    const skill = Math.round(100 * (row.skill[0] + (row.skill[1] - row.skill[0]) * rng())) / 100;
    const rateAud = randInt(rng, row.rate[0], row.rate[1]);
    const past = pick(rng, BIO_PAST);
    const quirk = pick(rng, BIO_QUIRK);
    out.push({ id: `cand-${day}-${i}`, name: `${short} ${pick(rng, SURNAMES)}`, short, bio: [past, quirk], level, skill, rateAud });
  }
  const taken = st.day === day ? new Set(st.taken) : new Set<string>();
  return out.filter((c) => !taken.has(c.id));
}

export function hire(save: SaveGame, candidateId: string, day: number): TxResult {
  if (!crewUnlocked(save.repPoints)) return refuse(save, `Hiring opens at Rep ${CREW_REP_LEVEL}.`);
  const st0 = staffOf(save);
  const cap = crewCap(save.repPoints);
  if (st0.hired.length >= cap) {
    const next = nextCapLevel(save.repPoints);
    return refuse(save, next ? `Crew’s full at ${cap}. Rep ${next} for another.` : 'Crew’s full.');
  }
  const cand = candidatesFor(save, day).find((c) => c.id === candidateId);
  if (!cand) return refuse(save, 'They’ve taken another job.');
  const { s, st } = cloneStaff(save);
  if (st.day !== day) { st.day = day; st.taken = []; }
  st.taken.push(cand.id);
  st.hired.push({ ...cand, id: `emp-${st.nextId}`, xp: 0, jobs: 0, hiredDay: day });
  st.nextId++;
  return { ok: true, save: s, costAud: 0 };
}

export function fire(save: SaveGame, employeeId: string): TxResult {
  const st0 = staffOf(save);
  const e = st0.hired.find((x) => x.id === employeeId);
  if (!e) return refuse(save, 'Not on the books.');
  if (st0.assignments.some((a) => a.employeeId === employeeId)) return refuse(save, `${e.short}’s out on a job. Call them back first.`);
  const { s, st } = cloneStaff(save);
  st.hired = st.hired.filter((x) => x.id !== employeeId);
  return { ok: true, save: s, costAud: 0 };
}

// ─── Assignment ─────────────────────────────────────────────────────────

/** uid → the crew member who has it out. */
export function gearOut(save: SaveGame): Map<string, string> {
  const st = staffOf(save);
  const out = new Map<string, string>();
  for (const a of st.assignments) {
    const who = st.hired.find((e) => e.id === a.employeeId)?.short ?? 'the crew';
    for (const u of a.gear) out.set(u, who);
  }
  return out;
}

export function isGearOut(save: SaveGame, uid: string): boolean {
  return staffOf(save).assignments.some((a) => a.gear.includes(uid));
}

export function assignmentFor(save: SaveGame, contractId: string): CrewAssignment | undefined {
  return staffOf(save).assignments.find((a) => a.contract.id === contractId);
}

export function employeeBusy(save: SaveGame, employeeId: string): boolean {
  return staffOf(save).assignments.some((a) => a.employeeId === employeeId);
}

export interface AssignOptions {
  models?: Record<string, EquipmentModel>;
  /** The player's job in progress: its contract can't be given away, nor its gear. */
  playerContractId?: string;
  playerGear?: readonly string[];
}

/**
 * Send a crew member out: a contract, an employee and the gear they take.
 * The gear must cover every task (P3's taskGearOk: the fit badge's "Not
 * enough gear" is a refusal here), must not be out already or on your job,
 * and must leave you a mower (DECISIONS.md: no deadlock with the only mower out).
 */
export function assignCrew(
  save: SaveGame, pickd: { contract: Contract; employeeId: string; gear: readonly string[] }, opts: AssignOptions = {},
): TxResult {
  const models = opts.models ?? MODELS;
  const st0 = staffOf(save);
  const e = st0.hired.find((x) => x.id === pickd.employeeId);
  if (!e) return refuse(save, 'Not on the books.');
  if (employeeBusy(save, e.id)) return refuse(save, `${e.short}’s already out.`);
  const c = pickd.contract;
  if (c.id === opts.playerContractId) return refuse(save, 'That one’s yours.');
  if (assignmentFor(save, c.id)) return refuse(save, 'Crew’s already on it.');
  if (c.minRepLevel > repLevel(save.repPoints)) return refuse(save, `Unlocks at rep level ${c.minRepLevel}.`);
  const gear = [...new Set(pickd.gear)];
  if (gear.length > CREW_GEAR_MAX) return refuse(save, `${e.short} can carry ${CREW_GEAR_MAX} things, tops.`);
  const out = gearOut(save);
  const items: OwnedEquipment[] = [];
  for (const u of gear) {
    const it = save.owned.find((o) => o.uid === u);
    if (!it || !models[it.modelKey]) return refuse(save, 'You don’t own that.');
    if (out.has(u)) return refuse(save, `Out with ${out.get(u)}.`);
    if (opts.playerGear?.includes(u)) return refuse(save, 'That’s on your ute.');
    items.push(it);
  }
  const gm = items.map((o) => models[o.modelKey]);
  const freeMowers = save.owned.filter((o) => models[o.modelKey]?.kind === 'mower' && !out.has(o.uid));
  if (c.tasks.some((t) => !taskGearOk(t.kind, gm))) {
    const needsMower = c.tasks.some((t) => t.kind === 'mow') && !gm.some((m) => m.kind === 'mower');
    if (needsMower && freeMowers.length <= 1) return refuse(save, 'Your only mower stays with you. A second one lets the crew mow.');
    return refuse(save, 'Not enough gear for the list.');
  }
  if (gm.some((m) => m.kind === 'mower') && !freeMowers.some((o) => !gear.includes(o.uid))) return refuse(save, 'Leave yourself a mower.');
  // Balance pass: an oval needs a ride-on, for the crew as for you.
  if (needsRideOn(c) && !gm.some((m) => m.kind === 'mower' && m.rideOn)) return refuse(save, `${NEEDS_RIDE_ON_COPY}. Send one with ${e.short}.`);
  const { s, st } = cloneStaff(save);
  st.assignments.push({ id: `job-${st.nextId}`, employeeId: e.id, contract: c, gear });
  st.nextId++;
  return { ok: true, save: s, costAud: 0 };
}

/** Call a crew member back before they start: no pay, no wages, gear home. */
export function recallCrew(save: SaveGame, assignmentId: string): TxResult {
  if (!staffOf(save).assignments.some((a) => a.id === assignmentId)) return refuse(save, 'Nobody’s out on that.');
  const { s, st } = cloneStaff(save);
  st.assignments = st.assignments.filter((a) => a.id !== assignmentId);
  return { ok: true, save: s, costAud: 0 };
}

// ─── Resolution ─────────────────────────────────────────────────────────

/** Par for a crew job with the gear they took (a no-mow job walks; par uses your first mower, as the sim parks it). */
export function crewPar(save: SaveGame, contract: Contract, base: Layout, gear: readonly string[], models: Record<string, EquipmentModel> = MODELS): number {
  const { layout, hazards } = contractLayout(base, contract);
  const mine = gear.map((u) => save.owned.find((o) => o.uid === u)).find((o) => o && models[o.modelKey]?.kind === 'mower');
  const first = save.owned.find((o) => models[o.modelKey]?.kind === 'mower');
  const mower = models[(mine ?? first)?.modelKey ?? 'redgum-46'] ?? MODELS['redgum-46'];
  return parSeconds(estimateParInput(layout, contract.tasks, hazards.lostBall), mower, contract.tasks);
}

/** Estimated wage for the assign sheet: par × (1.2 − skill/2) on the timesheet. */
export function estimateWage(par: number, e: Pick<Employee, 'skill' | 'rateAud'>): number {
  return crewWage(crewSeconds(par, e.skill), e.rateAud).wageAud;
}

/** The seed a crew job resolves with. */
export function crewJobSeed(st: StaffState, a: CrewAssignment): number {
  return mixSeed(mixSeed(st.seed, a.contract.seed >>> 0), hashString(a.id));
}

/**
 * Settle one crew job. `base` is the contract's layout (before mutations).
 * Deterministic for a save + assignment.
 */
export function resolveCrewJob(
  save: SaveGame, assignmentId: string, base: Layout, models: Record<string, EquipmentModel> = MODELS, opts: CrewOptions = {},
): CrewResult | null {
  const st = staffOf(save);
  const a = st.assignments.find((x) => x.id === assignmentId);
  const e = a && st.hired.find((x) => x.id === a.employeeId);
  if (!a || !e) return null;
  const c = a.contract;
  const rng = mulberry32(crewJobSeed(st, a));
  const { layout } = contractLayout(base, c);
  const par = crewPar(save, c, base, a.gear, models);
  const complexity = c.complexity;

  // Thoroughness: skill ± noise. Every task sits at it (the crew works the list evenly).
  const thoroughness = crewThoroughness(rng, e.skill);
  const tasks = c.tasks.map((t) => ({ kind: t.kind, label: t.label, target: t.target, pct: thoroughness, done: thoroughness >= t.target }));

  // Collisions: Poisson on (1 − skill), each one a billable prop (or, on a mowing job, sometimes the do-not-mow).
  const n = poisson(rng, collisionLambda(e.skill, complexity));
  const billable = layout.props.filter((p) => damageFor(p) !== 'none');
  const zones = c.tasks.some((t) => t.kind === 'mow') ? layout.zones.filter((z) => z.doNotMow) : [];
  const hits: { kind: PropKind; damage: 'minor' | 'major' }[] = [];
  const violationZones: ZoneId[] = [];
  for (let i = 0; i < n; i++) {
    if (zones.length && rng() < CREW_ZONE_SHARE) {
      violationZones.push(pick(rng, zones).id);
      continue;
    }
    if (!billable.length) continue;
    const p = pick(rng, billable);
    const d = damageFor(p);
    hits.push({ kind: p.kind, damage: d === 'major' ? 'major' : 'minor' });
  }
  const majors = hits.filter((h) => h.damage === 'major').length;
  const penaltiesAud = (hits.length - majors) * OBSTACLE_PENALTY_AUD + majors * MAJOR_HIT_PENALTY_AUD
    + violationZones.length * VIOLATION_PENALTY_AUD;

  // The player's contract maths from here.
  const seconds = crewSeconds(par, e.skill);
  const stars = starsFor(seconds, par);
  const feeAud = c.feeAud;
  const tp = tipPct(thoroughness);
  const tipAud = Math.round((feeAud * tp) / 100);
  const totalAud = Math.max(0, feeAud + tipAud - penaltiesAud);
  const repFull = repForStars(stars);

  // Fuel and wear on the gear they took.
  const minutes = seconds / 60;
  const kinds = new Set(c.tasks.map((t) => t.kind));
  const mowCells = layoutStats(layout).mowableCells;
  let fuelUsedL = 0;
  let fuelAud = 0;
  const gear: OwnedEquipment[] = [];
  for (const uid of a.gear) {
    const it = save.owned.find((o) => o.uid === uid);
    const m = it && models[it.modelKey];
    if (!it || !m) continue;
    const share = m.kind === 'mower' ? (kinds.has('mow') ? 1 : 0)
      : m.kind === 'trimmer' ? (kinds.has('trim') ? 0.4 : 0)
        : m.kind === 'blower' ? (kinds.has('blow') || kinds.has('collect') ? 0.4 : 0) : 0;
    const next = { ...it };
    if (m.power === 'petrol' && share > 0) {
      const burn = ((m.burnLph ?? 0) * share * seconds) / 3600;
      fuelUsedL += burn;
      // Short of fuel, they fill up on the road and bill you for it.
      if (burn > it.fuelL) fuelAud += (burn - it.fuelL) * FUEL_AUD_PER_L;
      next.fuelL = Math.max(0, it.fuelL - burn);
    }
    // Feature P7 (opts.battery): a battery tool runs its charge down (never below 0: they
    // top up on the road, free) and wears ×1.6.
    const wearX = opts.battery && m.power === 'battery' ? BATTERY_WEAR_X : 1;
    if (opts.battery && usesCharge(m) && share > 0) next.chargePct = Math.max(0, chargeOf(it) - ((share * seconds) / runSecondsOf(m)) * 100);
    if (m.power !== 'none' && share > 0) next.engineCondition = clamp01(it.engineCondition - CREW_ENGINE_WEAR_PER_MIN * minutes * share * wearX);
    if (m.kind === 'mower' && share > 0) next.bladeCondition = clamp01(it.bladeCondition - CREW_BLADE_WEAR_PER_CELL * mowCells);
    gear.push(next);
  }
  fuelAud = money(fuelAud);

  const result: JobResult = {
    contractId: c.id,
    layoutKey: c.layoutKey,
    abandoned: false,
    seconds,
    parSeconds: par,
    stars,
    tasks,
    thoroughness,
    feeAud,
    tipPct: tp,
    tipAud,
    obstacleHits: hits.length,
    violations: violationZones.length,
    hits,
    violationZones,
    mulched: 0,
    mulchedKinds: [],
    penaltiesAud,
    totalAud,
    repPoints: repFull,
    fuelUsedL: round2(fuelUsedL),
    gear,
  };
  const { hours, wageAud } = crewWage(seconds, e.rateAud);
  const lines = tipBreakdown(result).deductions.map((l) => `${e.short} ${l.reason}${times(l.count)}`);
  return {
    assignmentId: a.id,
    employeeId: e.id,
    short: e.short,
    contract: c,
    result,
    hours,
    wageAud,
    fuelAud,
    netAud: money(totalAud - wageAud - fuelAud),
    repPoints: Math.floor(repFull / 2),
    lines,
  };
}

/** Feature P7: battery charge and ×1.6 wear on crew gear. Off when absent; the store passes FEATURES.battery. */
export interface CrewOptions {
  battery?: boolean;
}

/** Settle every active assignment (skipping any whose layout is gone). */
export function resolveAllCrew(
  save: SaveGame, layoutOf: (key: string) => Layout | undefined, models: Record<string, EquipmentModel> = MODELS, opts: CrewOptions = {},
): CrewResult[] {
  const out: CrewResult[] = [];
  for (const a of staffOf(save).assignments) {
    const base = layoutOf(a.contract.layoutKey);
    const r = base && resolveCrewJob(save, a.id, base, models, opts);
    if (r) out.push(r);
  }
  return out;
}

/** Your job as a payouts row. */
export function playerPayout(id: string, r: JobResult): PayoutRow {
  return {
    id, who: 'you', name: 'You', contractId: r.contractId, layoutKey: r.layoutKey,
    feeAud: r.feeAud, tipPct: r.tipPct, tipAud: r.tipAud, penaltiesAud: r.penaltiesAud,
    wageAud: 0, fuelAud: 0, netAud: r.totalAud, stars: r.stars, repPoints: r.repPoints, abandoned: r.abandoned,
  };
}

/**
 * Bank crew jobs (applyJobResult stays the player's): net pay into the
 * account, half rep, fuel and wear onto the gear, the gear home, experience
 * and level-ups, and one payouts row each. With `you`, your job goes on the
 * payouts board first. Unknown assignments are ignored.
 */
export function applyCrewResults(save: SaveGame, results: readonly CrewResult[], you?: JobResult): SaveGame {
  const { s, st } = cloneStaff(save);
  if (you) st.payouts.push(playerPayout(`pay-${st.nextId++}`, you));
  for (const r of results) {
    const a = st.assignments.find((x) => x.id === r.assignmentId);
    if (!a) continue;
    st.assignments = st.assignments.filter((x) => x.id !== a.id);
    s.cash = money(s.cash + r.netAud);
    s.repPoints += r.repPoints;
    for (const g of r.result.gear ?? []) {
      const it = s.owned.find((o) => o.uid === g.uid);
      if (!it) continue;
      it.engineCondition = clamp01(g.engineCondition);
      it.bladeCondition = clamp01(g.bladeCondition);
      it.fuelL = Math.max(0, g.fuelL);
      if (g.chargePct !== undefined) it.chargePct = chargeOf(g);
    }
    const e = st.hired.find((x) => x.id === r.employeeId);
    if (e) {
      e.jobs += 1;
      e.xp += r.result.stars === 3 ? 2 : 1;
      while (e.level < MAX_CREW_LEVEL && e.xp >= CREW_LEVEL_XP[e.level]) {
        e.level += 1;
        e.skill = Math.min(SKILL_MAX, Math.round(100 * (e.skill + LEVEL_SKILL_STEP)) / 100);
        e.rateAud += LEVEL_RATE_STEP;
      }
    }
    const res = r.result;
    st.payouts.push({
      id: `pay-${st.nextId++}`, who: 'crew', employeeId: r.employeeId, name: r.short,
      contractId: res.contractId, layoutKey: res.layoutKey, feeAud: res.feeAud, tipPct: res.tipPct, tipAud: res.tipAud,
      penaltiesAud: res.penaltiesAud, wageAud: r.wageAud, fuelAud: r.fuelAud, netAud: r.netAud,
      stars: res.stars, repPoints: r.repPoints, abandoned: false, lines: r.lines.slice(),
    });
  }
  if (st.payouts.length > PAYOUTS_KEEP) st.payouts = st.payouts.slice(-PAYOUTS_KEEP);
  return s;
}

// ─── Payouts board ──────────────────────────────────────────────────────

/**
 * "Dazza's getting 40% tips. Maybe stop hitting fences." when a crew
 * member's average tip % beats yours by NUDGE_GAP_PCT or more (over the
 * board's rows). The second line follows your own damage.
 */
export function crewNudge(rows: readonly PayoutRow[]): string | null {
  const mine = rows.filter((r) => r.who === 'you' && !r.abandoned);
  const crew = rows.filter((r) => r.who === 'crew');
  if (!mine.length || !crew.length) return null;
  const avg = (xs: readonly PayoutRow[]) => xs.reduce((a, r) => a + r.tipPct, 0) / xs.length;
  const yours = avg(mine);
  const by = new Map<string, PayoutRow[]>();
  for (const r of crew) by.set(r.name, [...(by.get(r.name) ?? []), r]);
  let best: { name: string; pct: number } | null = null;
  for (const [name, xs] of by) {
    const pct = avg(xs);
    if (!best || pct > best.pct) best = { name, pct };
  }
  if (!best || best.pct - yours < NUDGE_GAP_PCT) return null;
  const hitting = mine.some((r) => r.penaltiesAud > 0);
  return `${best.name}’s getting ${Math.round(best.pct)}% tips. ${hitting ? 'Maybe stop hitting fences.' : 'Maybe go the extra mile.'}`;
}
